import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AdminOrganization } from '@central/contracts';
import { Button } from '@/components/Button';
import {
  alterarOrganizacao,
  apagarOrganizacao,
  chavesDeAdministracao,
  criarOrganizacao,
  listarOrganizacoes,
} from '@/features/admin/admin.api';
import {
  AcoesDaLinha,
  Cabecalho,
  Campo,
  CascaDeCatalogo,
  Celula,
  CLASSE_DE_CAMPO,
  ConfirmacaoDeCatalogo,
  ContagemDeUso,
  EtiquetaDeAtividade,
  Linha,
  LinhaDeCabecalho,
  useEscritaDeCatalogo,
  type Intencao,
} from '@/features/admin/componentes';
import { Modal } from '@/features/tickets/Modal';

/**
 * Organizações atendidas.
 *
 * É o único dos cinco catálogos sem chave — a identidade estável de uma
 * organização é o CNPJ, e ela não é citada por chave em automação. Por isso é
 * também o único em que o identificador é editável: CNPJ digitado errado
 * precisa poder ser consertado.
 */
const TIPOS: Array<[AdminOrganization['type'], string]> = [
  ['CUSTOMER', 'Cliente'],
  ['INTERNAL', 'Interna'],
  ['SUPPLIER', 'Fornecedor'],
  ['PARTNER', 'Parceiro'],
];

const ROTULO_DE_TIPO = new Map(TIPOS);

export function OrganizationsPage() {
  const consulta = useQuery({
    queryKey: chavesDeAdministracao.organizacoes,
    queryFn: listarOrganizacoes,
  });
  const organizacoes = consulta.data?.items ?? [];

  const [criando, setCriando] = useState(false);
  const [editando, setEditando] = useState<AdminOrganization | null>(null);
  const [confirmando, setConfirmando] = useState<{
    registro: AdminOrganization;
    intencao: Intencao;
  } | null>(null);

  const atividade = useEscritaDeCatalogo(
    ({ id, active, version }: { id: string; active: boolean; version: number }) =>
      alterarOrganizacao(id, { active, version }),
    () => setConfirmando(null),
  );
  const remocao = useEscritaDeCatalogo(
    (id: string) => apagarOrganizacao(id),
    () => setConfirmando(null),
  );

  const ocupado = atividade.mutacao.isPending || remocao.mutacao.isPending;

  return (
    <>
      <CascaDeCatalogo
        titulo="Organizações"
        resumo="Os clientes atendidos. O domínio de e-mail associa o solicitante automaticamente quando o chamado chega pela caixa."
        rotuloDoBotao="Nova organização"
        aoCriar={() => setCriando(true)}
        consulta={consulta}
        vazio="Nenhuma organização cadastrada. Todo chamado precisa de uma."
      >
        <LinhaDeCabecalho>
          <Cabecalho>Organização</Cabecalho>
          <Cabecalho>CNPJ</Cabecalho>
          <Cabecalho>Domínio</Cabecalho>
          <Cabecalho>Tipo</Cabecalho>
          <Cabecalho>Em uso</Cabecalho>
          <Cabecalho>Estado</Cabecalho>
          <Cabecalho className="text-right">Ações</Cabecalho>
        </LinhaDeCabecalho>

        <tbody>
          {organizacoes.map((organizacao) => (
            <Linha key={organizacao.id} inativo={!organizacao.active}>
              <Celula>
                <span className="font-medium">{organizacao.name}</span>
                {organizacao.tradeName ? (
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {organizacao.legalName}
                  </span>
                ) : null}
              </Celula>
              <Celula className="font-mono text-xs text-muted-foreground">
                {organizacao.taxId ?? '—'}
              </Celula>
              <Celula className="text-xs text-muted-foreground">
                {organizacao.domain ?? '—'}
              </Celula>
              <Celula className="text-xs">
                {ROTULO_DE_TIPO.get(organizacao.type) ?? organizacao.type}
              </Celula>
              <Celula>
                <ContagemDeUso uso={organizacao.usage} />
              </Celula>
              <Celula>
                <EtiquetaDeAtividade ativo={organizacao.active} />
              </Celula>
              <Celula>
                <AcoesDaLinha
                  registro={organizacao}
                  entidade="organização"
                  ocupado={ocupado}
                  aoEditar={() => setEditando(organizacao)}
                  aoAlternarAtividade={() => {
                    if (!organizacao.active) {
                      atividade.mutacao.mutate({
                        id: organizacao.id,
                        active: true,
                        version: organizacao.version,
                      });
                      return;
                    }
                    setConfirmando({ registro: organizacao, intencao: 'desativar' });
                  }}
                  aoRemover={() => setConfirmando({ registro: organizacao, intencao: 'remover' })}
                />
              </Celula>
            </Linha>
          ))}
        </tbody>
      </CascaDeCatalogo>

      <DialogoDeOrganizacao
        aberto={criando || editando !== null}
        organizacao={editando}
        aoFechar={() => {
          setCriando(false);
          setEditando(null);
        }}
      />

      <ConfirmacaoDeCatalogo
        aberto={confirmando !== null}
        intencao={confirmando?.intencao ?? 'desativar'}
        entidade="organização"
        registro={confirmando?.registro ?? null}
        erro={atividade.erro ?? remocao.erro}
        ocupado={ocupado}
        aoFechar={() => setConfirmando(null)}
        aoConfirmar={() => {
          if (!confirmando) return;
          if (confirmando.intencao === 'remover') {
            remocao.mutacao.mutate(confirmando.registro.id);
            return;
          }
          atividade.mutacao.mutate({
            id: confirmando.registro.id,
            active: false,
            version: confirmando.registro.version,
          });
        }}
      />
    </>
  );
}

function DialogoDeOrganizacao({
  aberto,
  organizacao,
  aoFechar,
}: {
  aberto: boolean;
  organizacao: AdminOrganization | null;
  aoFechar: () => void;
}) {
  const editando = organizacao !== null;

  const [razaoSocial, setRazaoSocial] = useState('');
  const [nomeFantasia, setNomeFantasia] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [dominio, setDominio] = useState('');
  const [tipo, setTipo] = useState<AdminOrganization['type']>('CUSTOMER');
  const [semeado, setSemeado] = useState<string | null>(null);

  const identidade = organizacao?.id ?? (aberto ? 'nova' : null);
  if (aberto && semeado !== identidade) {
    setSemeado(identidade);
    setRazaoSocial(organizacao?.legalName ?? '');
    setNomeFantasia(organizacao?.tradeName ?? '');
    setCnpj(organizacao?.taxId ?? '');
    setDominio(organizacao?.domain ?? '');
    setTipo(organizacao?.type ?? 'CUSTOMER');
  }
  if (!aberto && semeado !== null) setSemeado(null);

  const escrita = useEscritaDeCatalogo(async () => {
    if (organizacao) {
      // Modo edição: envia null para limpar CNPJ/domínio quando o campo é
      // esvaziado — omitir significaria "não alterar", e o usuário quer limpar.
      return alterarOrganizacao(organizacao.id, {
        legalName: razaoSocial.trim(),
        tradeName: nomeFantasia.trim() || null,
        taxId: cnpj.trim() || null,
        domain: dominio.trim().toLowerCase() || null,
        type: tipo,
        version: organizacao.version,
      });
    }
    // Modo criação: omite campos opcionais vazios.
    const corpo = {
      legalName: razaoSocial.trim(),
      ...(nomeFantasia.trim() ? { tradeName: nomeFantasia.trim() } : {}),
      ...(cnpj.trim() ? { taxId: cnpj.trim() } : {}),
      ...(dominio.trim() ? { domain: dominio.trim().toLowerCase() } : {}),
      type: tipo,
    };
    return criarOrganizacao(corpo);
  }, aoFechar);

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    escrita.mutacao.mutate();
  }

  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={editando ? `Editar “${organizacao.name}”` : 'Nova organização'}
      descricao="O nome fantasia é o que aparece nas telas; a razão social fica para documento."
    >
      <form onSubmit={enviar} className="space-y-4 px-5 py-4">
        <Campo id="org-razao" rotulo="Razão social">
          <input
            id="org-razao"
            value={razaoSocial}
            onChange={(evento) => setRazaoSocial(evento.target.value)}
            required
            minLength={2}
            maxLength={200}
            className={CLASSE_DE_CAMPO}
          />
        </Campo>

        <Campo id="org-fantasia" rotulo="Nome fantasia" opcional>
          <input
            id="org-fantasia"
            value={nomeFantasia}
            onChange={(evento) => setNomeFantasia(evento.target.value)}
            maxLength={200}
            className={CLASSE_DE_CAMPO}
          />
        </Campo>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo id="org-cnpj" rotulo="CNPJ" opcional>
            <input
              id="org-cnpj"
              value={cnpj}
              onChange={(evento) => setCnpj(evento.target.value)}
              maxLength={20}
              className={`${CLASSE_DE_CAMPO} font-mono text-xs`}
            />
          </Campo>

          <Campo id="org-tipo" rotulo="Tipo">
            <select
              id="org-tipo"
              value={tipo}
              onChange={(evento) => setTipo(evento.target.value as AdminOrganization['type'])}
              className={CLASSE_DE_CAMPO}
            >
              {TIPOS.map(([valor, rotulo]) => (
                <option key={valor} value={valor}>
                  {rotulo}
                </option>
              ))}
            </select>
          </Campo>
        </div>

        <Campo
          id="org-dominio"
          rotulo="Domínio de e-mail"
          opcional
          dica="Só o domínio: contoso.com.br, sem @ e sem https://."
        >
          <input
            id="org-dominio"
            value={dominio}
            onChange={(evento) => setDominio(evento.target.value)}
            placeholder="contoso.com.br"
            maxLength={253}
            className={CLASSE_DE_CAMPO}
          />
        </Campo>

        {escrita.erro ? (
          <p role="alert" className="text-sm text-destructive">
            {escrita.erro}
          </p>
        ) : null}

        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <Button type="button" variant="secondary" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button type="submit" loading={escrita.mutacao.isPending}>
            {editando ? 'Salvar' : 'Criar organização'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
